import { useState, useEffect } from 'react';
import { apiV2 } from '../lib/baseAxios';
import logger from '../lib/logger';

// Output languages - matches transcription languages (excluding Auto)
export const OUTPUT_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'ja', name: 'Japanese' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'it', name: 'Italian' },
  { code: 'nl', name: 'Dutch' },
  { code: 'af', name: 'Afrikaans' },
  { code: 'ar', name: 'Arabic' },
  { code: 'hy', name: 'Armenian' },
  { code: 'az', name: 'Azerbaijani' },
  { code: 'be', name: 'Belarusian' },
  { code: 'bs', name: 'Bosnian' },
  { code: 'bg', name: 'Bulgarian' },
  { code: 'ca', name: 'Catalan' },
  { code: 'zh', name: 'Chinese' },
  { code: 'hr', name: 'Croatian' },
  { code: 'cs', name: 'Czech' },
  { code: 'da', name: 'Danish' },
  { code: 'et', name: 'Estonian' },
  { code: 'fi', name: 'Finnish' },
  { code: 'gl', name: 'Galician' },
  { code: 'el', name: 'Greek' },
  { code: 'he', name: 'Hebrew' },
  { code: 'hi', name: 'Hindi' },
  { code: 'hu', name: 'Hungarian' },
  { code: 'is', name: 'Icelandic' },
  { code: 'id', name: 'Indonesian' },
  { code: 'kn', name: 'Kannada' },
  { code: 'kk', name: 'Kazakh' },
  { code: 'ko', name: 'Korean' },
  { code: 'lv', name: 'Latvian' },
  { code: 'lt', name: 'Lithuanian' },
  { code: 'mk', name: 'Macedonian' },
  { code: 'ms', name: 'Malay' },
  { code: 'mr', name: 'Marathi' },
  { code: 'mi', name: 'Maori' },
  { code: 'ne', name: 'Nepali' },
  { code: 'no', name: 'Norwegian' },
  { code: 'fa', name: 'Persian' },
  { code: 'pl', name: 'Polish' },
  { code: 'ro', name: 'Romanian' },
  { code: 'ru', name: 'Russian' },
  { code: 'sr', name: 'Serbian' },
  { code: 'sk', name: 'Slovak' },
  { code: 'sl', name: 'Slovenian' },
  { code: 'sw', name: 'Swahili' },
  { code: 'sv', name: 'Swedish' },
  { code: 'tl', name: 'Tagalog' },
  { code: 'ta', name: 'Tamil' },
  { code: 'th', name: 'Thai' },
  { code: 'tr', name: 'Turkish' },
  { code: 'uk', name: 'Ukrainian' },
  { code: 'ur', name: 'Urdu' },
  { code: 'vi', name: 'Vietnamese' },
  { code: 'cy', name: 'Welsh' },
];

// Get browser language, fallback to 'en' if not supported
const getBrowserLanguage = (): string => {
  const browserLang = navigator.language.split('-')[0];
  return (
    OUTPUT_LANGUAGES.find((lang) => lang.code === browserLang)?.code || 'en'
  );
};

// Convert language code to full language name using existing OUTPUT_LANGUAGES array
const getLanguageName = (languageCode: string): string => {
  const language = OUTPUT_LANGUAGES.find(
    (lang) => lang.code === languageCode.toLowerCase()
  );
  return language?.name || 'English';
};

// Get current output language from backend
export const getCurrentOutputLanguage = async (): Promise<string> => {
  try {
    const response = await apiV2.get('/settings/output-language');
    if (response.data?.success && response.data?.data?.outputLanguage) {
      return response.data.data.outputLanguage;
    }
  } catch (err) {
    logger.error('Failed to fetch output language:', err);
  }
  return getBrowserLanguage();
};

// Get current output language name (full name, not code)
export const getCurrentOutputLanguageName = async (): Promise<string> => {
  const languageCode = await getCurrentOutputLanguage();
  return getLanguageName(languageCode);
};

// Save output language to backend
export const saveOutputLanguage = async (
  language: string
): Promise<boolean> => {
  try {
    const response = await apiV2.post('/settings/output-language', {
      outputLanguage: language,
    });
    return response.data?.success || false;
  } catch (err) {
    logger.error('Failed to save output language:', err);
    return false;
  }
};

// Hook for managing output language state
export const useOutputLanguage = () => {
  const [outputLanguage, setOutputLanguage] = useState<string>(
    getBrowserLanguage()
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load initial value from backend
  useEffect(() => {
    const loadOutputLanguage = async () => {
      const language = await getCurrentOutputLanguage();
      setOutputLanguage(language);
    };
    loadOutputLanguage();
  }, []);

  // Update output language
  const updateOutputLanguage = async (language: string): Promise<boolean> => {
    setLoading(true);
    setError(null);

    try {
      const success = await saveOutputLanguage(language);
      if (success) {
        setOutputLanguage(language);
        return true;
      }
      setError('Failed to update output language');
      return false;
    } catch (err) {
      setError('Failed to update output language');
      logger.error('Error updating output language:', err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Get current language name
  const currentLanguageName =
    OUTPUT_LANGUAGES.find((lang) => lang.code === outputLanguage)?.name ||
    'English';

  return {
    outputLanguage,
    currentLanguageName,
    loading,
    error,
    updateOutputLanguage,
    clearError: () => setError(null),
  };
};
